import type { HappyHourView } from 'shared-models/browser';

const JERUSALEM_TZ = 'Asia/Jerusalem';

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function parseTimeToMinutes(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)/.exec(value);
  if (!match) {
    return null;
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

// Summary: Asia/Jerusalem wall-clock day-of-week (0=Sun) and minutes-since-midnight from a Date.
function jerusalemDayAndMinutes(now: Date): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: JERUSALEM_TZ,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const lookup = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const day = WEEKDAY_INDEX[lookup('weekday')] ?? 0;
  return { day, minutes: Number(lookup('hour')) * 60 + Number(lookup('minute')) };
}

// Summary: mirror server HH window (Asia/Jerusalem, start<=now<=end inclusive, no midnight-cross).
export function isHappyHourEffective(happyHour: HappyHourView | null, now: Date): boolean {
  if (!happyHour || !happyHour.active) {
    return false;
  }
  if (happyHour.discountPct == null || happyHour.discountPct <= 0) {
    return false;
  }
  if (!happyHour.startTime || !happyHour.endTime) {
    return false;
  }
  const start = parseTimeToMinutes(happyHour.startTime);
  const end = parseTimeToMinutes(happyHour.endTime);
  if (start == null || end == null) {
    return false;
  }
  const { day, minutes } = jerusalemDayAndMinutes(now);
  if (!happyHour.activeDays.includes(day)) {
    return false;
  }
  // Mirrors the order SQL: a day listed in dayOverrides uses its own window, otherwise the
  // tenant-wide pair.
  const override = happyHour.dayOverrides?.find((entry) => entry.day === day);
  const effectiveStart = override ? parseTimeToMinutes(override.startTime) ?? start : start;
  const effectiveEnd = override ? parseTimeToMinutes(override.endTime) ?? end : end;
  return minutes >= effectiveStart && minutes <= effectiveEnd;
}
